import { useMemo } from 'react'

import {
  Maybe,
  SalesStatus,
  StoreItemDetailFragment,
} from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'

type UseStoreButtonProps = {
  item?: Maybe<StoreItemDetailFragment>
  isLoggedIn: boolean
  isNft?: boolean
  userId?: string
  isWalletConnect?: boolean
  purchaseLimitByUser: number
}

export const useStoreButton = ({
  isLoggedIn,
  isNft,
  userId,
  isWalletConnect,
  item,
  purchaseLimitByUser,
}: UseStoreButtonProps) => {
  const { t } = useTranslation('storeItemDetail')

  // 1度に購入できる個数。制限が設定されてなければ10
  const maximumPurchasableNumber = useMemo(() => {
    if (item?.preSale?.lottery?.purchasableCount) {
      return item.preSale.lottery.purchasableCount
    }
    if (item?.preSale?.firstComeFirstServed?.purchasableCount) {
      return item.preSale.firstComeFirstServed.purchasableCount
    }
    if (item?.purchaseLimitPerOrder) {
      return item?.purchaseLimitPerOrder
    }
    if (purchaseLimitByUser) {
      return purchaseLimitByUser
    }
    return 10
  }, [
    item?.preSale?.firstComeFirstServed?.purchasableCount,
    item?.preSale?.lottery?.purchasableCount,
    item?.purchaseLimitPerOrder,
    purchaseLimitByUser,
  ])

  // 対象者限定かつユーザーが対象者でない場合
  const restrected =
    !!item?.purchasableUsers?.length &&
    !item.purchasableUsers.map(({ userId }) => userId).includes(`${userId}`)

  const { label, disabled } = useMemo(() => {
    if (!item) {
      return { label: '', disabled: true }
    }
    if (isLoggedIn && isNft && !isWalletConnect) {
      return { label: t('purchaseNow'), disabled: true }
    }
    if (
      !!item.purchaseLimitByUser &&
      !!item.purchasedByPointNumber &&
      item.purchaseLimitByUser <= item.purchasedByPointNumber
    ) {
      return {
        label: t(item.price > 0 ? 'purchased' : 'earned'),
        disabled: true,
      }
    }
    if (item.salesStatus === SalesStatus.Stopped) {
      return { label: t('stopSelling'), disabled: true }
    }
    if (item.isResaleEnable) {
      return {
        label: t(item.price > 0 ? 'purchaseNow' : 'openAcquire'),
        disabled: false,
      }
    }
    if (item.issuedNumber && +(item.remainingNumber || 0) === 0) {
      return { label: t('soldOut'), disabled: true }
    }
    return {
      label: t(item.price > 0 ? 'purchaseNow' : 'openAcquire'),
      disabled: false,
    }
  }, [item, isLoggedIn, isNft, isWalletConnect, t])

  return { label, disabled, maximumPurchasableNumber, restrected }
}
